const KEY_PREFIX = 'PEOPLE_DASH';

const getStorageKey = (key: string): string => {
  return `${KEY_PREFIX}_${key.replace(/-/g, '_').toUpperCase()}`;
};

/**
 * Keys owned by third-party SDKs that must survive a storage clear.
 * Mixpanel keeps its `$device_id`/`distinct_id` under `mp_<token>_mixpanel`; wiping it on
 * logout makes the SDK mint a brand new anonymous device on the next page load, which
 * inflates Monthly Tracked Users and breaks identity merge for returning users.
 *
 * These keys are skipped by removing every other key one by one, never by calling
 * `storage.clear()` and writing them back: a full clear fires a global storage event with a
 * null key in the other tabs, and leaves a window where the preserved key does not exist.
 */
const PRESERVED_KEY_PREFIXES = ['mp_'];

const isPreservedKey = (key: string): boolean =>
  PRESERVED_KEY_PREFIXES.some(prefix => key.startsWith(prefix));

const getRemovableKeys = (storage: Storage): string[] => {
  const keys: string[] = [];

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key && !isPreservedKey(key)) {
      keys.push(key);
    }
  }

  return keys;
};

export const createStorage = (storage: Storage) => ({
  setItem(key: string, value: any): void {
    try {
      const storageKey = getStorageKey(key);
      const data = JSON.stringify(value);
      storage.setItem(storageKey, data);
    } catch (error) {
      console.error('Error setting data in storage:', error);
    }
  },
  getItem(key: string): any {
    try {
      const storageKey = getStorageKey(key);
      const data = storage.getItem(storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting data from storage:', error);
      return null;
    }
  },
  removeItem(key: string): void {
    try {
      const storageKey = getStorageKey(key);
      storage.removeItem(storageKey);
    } catch (error) {
      console.error('Error removing data from storage:', error);
    }
  },
  clear(): void {
    try {
      getRemovableKeys(storage).forEach(key => storage.removeItem(key));
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }
});

export const SessionStorage = createStorage(sessionStorage);
export const LocalStorage = createStorage(localStorage);

export const clearStorage = (): void => {
  LocalStorage.clear();
  SessionStorage.clear();
};

export const setTokenStorage = (token = ''): void => {
  LocalStorage.setItem('token', token);
};

export const setCsrfToken = (csrfToken = ''): void => {
  LocalStorage.setItem('csrf_token', csrfToken);
};

export const setCurrentUser = ({ user_name = '', user_email = '', user_id = '' }): void => {
  LocalStorage.setItem('user', { user_name, user_email, user_id });
};

export const setCurrentAccount = (account = ''): void => {
  SessionStorage.setItem('current_account', account);
};

export const setCurrentOrganization = ({ organization = '', organizationName = '', originAppName = '' }): void => {
  LocalStorage.setItem('organization', { organization, organizationName, originAppName });
};

export const setContactCandidate = (contactCandidate = {}): void => {
  SessionStorage.setItem('contact_candidate', contactCandidate);
};

export const setCandidateInfos = (candidateInfos = {}): void => {
  SessionStorage.setItem('candidate_infos', candidateInfos);
};

export const getTokenStorage = (): string | null => {
  return LocalStorage.getItem('token');
};

export const getCsrfToken = (): string | null => {
  return LocalStorage.getItem('csrf_token');
};

export const getCurrentUser = (): {
  user_name: string | null;
  user_email: string | null;
  user_id: string | null;
} => {
  const user = LocalStorage.getItem('user') ?? {};

  return {
    user_name: user?.user_name ?? '',
    user_email: user?.user_email ?? '',
    user_id: user?.user_id ?? ''
  };
};

export const getCurrentAccount = (): string | null => {
  return SessionStorage.getItem('current_account');
};

export const getCurrentOrganization = (): {
  organization: string | null;
  organizationName: string | null;
  originAppName: string | null;
} => {
  const org = LocalStorage.getItem('organization') ?? {};

  return {
    organization: org?.organization ?? '',
    organizationName: org?.organizationName ?? '',
    originAppName: org?.originAppName ?? ''
  };
};

export const getContactCandidate = (): any => {
  return SessionStorage.getItem('contact_candidate');
};

export const getCandidateInfos = (): any => {
  return SessionStorage.getItem('candidate_infos');
};

export const removeContactDetailsStorage = (): void => {
  LocalStorage.removeItem('contact_candidate');
  SessionStorage.removeItem('contact_candidate');

  LocalStorage.removeItem('candidate_infos');
  SessionStorage.removeItem('candidate_infos');

  LocalStorage.removeItem('contactsList');
  SessionStorage.removeItem('contactsList');
};

export const setOctopusContact = (currentPosition: {
  name: string;
  cpf?: string | null;
  email: string;
  phoneNumber: string;
  positionId?: string;
  unitId?: string;
}): { routeParams: { p?: string; u?: string } } => {
  SessionStorage.removeItem('octopus_contact');

  const contact = {
    name: currentPosition.name,
    cpf: currentPosition.cpf ?? null,
    email: currentPosition.email,
    phoneNumber: currentPosition.phoneNumber
  };

  const hasContactInfo = Object.values(contact).some(value => !!value);

  if (hasContactInfo) {
    SessionStorage.setItem('octopus_contact', contact);
  }

  const routeParams: { p?: string; u?: string } = {};
  if (currentPosition.positionId) routeParams.p = currentPosition.positionId;
  if (currentPosition.unitId) routeParams.u = currentPosition.unitId;

  return { routeParams };
};
